import React, {
  useState,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import {
  LinkIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CpuChipIcon,
  EyeSlashIcon,
  ChartBarSquareIcon,
  TableCellsIcon,
  InformationCircleIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import KnowledgeGraphAssociations from "../../components/Genetabs/KnowledgeGraphAssociations";
import ExpressionSummaries from "../../components/Genetabs/ExpressionSummaries";
import KnownDatabaseLink from "../../components/Genetabs/KnownDatabaseLink";
import { TargetXHeader } from "../../components/TargetXHeader";
import { useTheme } from "../../theme/useTheme";
import { API_BASE_URL } from "../../config";
import { Mechanisms } from "../../components/Mechanisms";
import { ExpressionProfile } from "../../components/ExpressionProfile/ExpressionProfile";
const LiteratureExplorer = React.lazy(
  () => import("../../components/LiteratureExplorer/LiteratureExplorer"),
);
import {
  scrnaseqapi,
  firstUpperCase,
  diseaseassociationsData,
  calculateTIssueExpression,
} from "../../utils/utils";
import { genepathwayassociationsColumns } from "../../utils/tableheaders";
import {
  geneDiseaseApi,
  type GeneAutocompleteResult,
} from "../../services/geneDiseaseClient";
import {
  useDiseaseTaxonomyRoots,
  useDiseaseTaxonomy,
} from "../../hooks/useDiseaseTaxonomy";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// Tab definitions similar to TargetX platform
interface TabConfig {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
}

const ANALYSIS_TABS: TabConfig[] = [
  {
    id: "knowledge-graph-associations",
    title: "Knowledge Graph Associations",
    icon: DocumentTextIcon,
    color: "blue",
    description: "Basic gene information and annotations",
  },
  {
    id: "expression-summaries",
    title: "Expression Summaries",
    icon: ChartBarSquareIcon,
    color: "blue",
    description: "Basic gene Expression Summaries",
  },
  {
    id: "expression-profile",
    title: "Single Cell Perturbation Predictions",
    icon: ChartBarIcon,
    color: "green",
    description: "Single Cell Perturbation Predictions",
  },
  {
    id: "mechanisms",
    title: "Mechanisms identified based on Literature",
    icon: CpuChipIcon,
    color: "orange",
    description: "Mechanisms identified based on Literature",
  },
  {
    id: "literature-explorer",
    title: "Literature Explorer",
    icon: BookOpenIcon,
    color: "blue",
    description: "AI-powered scientific literature exploration",
  },
];

const TA_TO_MONDO_ROOT: Record<string, string> = {
  cardiovascular: "MONDO:0004995",
  renal: "MONDO:0005240",
};

export default function GeneDiseaseLinkFixed() {
  // State management
  const therapeuticAreaValues = [
    {
      value: "cardiovascular",
      label: "Cardiovascular",
      internalValue: "heart",
      disabled: false,
    },
    {
      value: "renal",
      label: "Renal",
      internalValue: "kidney",
      disabled: false,
    },
    {
      value: "immunology",
      label: "Immunology",
      internalValue: "immunology",
      disabled: true,
    },
    {
      value: "oncology",
      label: "Oncology",
      internalValue: "cancer",
      disabled: true,
    },
  ];
  const [geneQuery, setGeneQuery] = useState("");
  const [therapeuticArea, setTherapeuticArea] = useState("");
  const [selectedGene, setSelectedGene] = useState<string>("");
  const [geneAutocompleteResults, setGeneAutocompleteResults] = useState<
    GeneAutocompleteResult[]
  >([]);
  const [showGeneAutocomplete, setShowGeneAutocomplete] = useState(false);
  const [geneAutocompleteIndex, setGeneAutocompleteIndex] = useState(-1);
  const [geneAutocompleteLoading, setGeneAutocompleteLoading] = useState(false);
  const debouncedGeneQuery = useDebounce(geneQuery, 250);
  const geneAutocompleteContainerRef = useRef<HTMLDivElement>(null);
  const geneInputRef = useRef<HTMLInputElement>(null);
  /** After picking from the list, debounced refetch would reopen the panel — skip until user types again. */
  const suppressGeneAutocompleteRef = useRef(false);
  /** Last successful non-empty suggestions; shown dimmed while the next request loads so the panel never flashes empty. */
  const geneAutocompleteLastHitsRef = useRef<GeneAutocompleteResult[]>([]);
  const analysisTabNavRef = useRef<HTMLDivElement>(null);
  const [analysisTabNavScroll, setAnalysisTabNavScroll] = useState({
    hasOverflow: false,
    canLeft: false,
    canRight: false,
  });
  const [activeTab, setActiveTab] = useState<string>(
    "knowledge-graph-associations",
  );
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [resultsVisible, setResultsVisible] = useState(true);
  const {
    setCollapsed,
    setScrnaseqdata,
    setRadioval,
    setTabdata,
    setLoading,
  } = useTheme();
  const [generesponse, setGeneresponse] = useState<any>({
    genedetails: { title: "", content: "" },
    kgedgeprediction: [],
    knowndatabaselink: [],
    singlecellperturbationprediction: "",
  });

  // ── MONDO Disease Taxonomy ─────────────────────────────────
  const activeMondoRootId = TA_TO_MONDO_ROOT[therapeuticArea] ?? undefined;
  const [selectedDiseaseIds, setSelectedDiseaseIds] = useState<Set<number>>(new Set());
  // submittedDiseases: "" means "All" (default); non-empty after user clicks Submit
  const [submittedDiseases, setSubmittedDiseases] = useState<string>("");
  // Refresh counter to force API refetch when needed (e.g., clearing all diseases)
  const [diseaseRefreshKey, setDiseaseRefreshKey] = useState(0);

  // Pre-fetch roots on mount (populates IndexedDB for future use)
  useDiseaseTaxonomyRoots();

  // Fetch taxonomy for the active therapeutic area
  const { data: taxonomyNodes } = useDiseaseTaxonomy(activeMondoRootId);

  // Clear disease selection when therapeutic area changes
  useEffect(() => {
    setSelectedDiseaseIds(new Set());
    setSubmittedDiseases(""); // Reset to "All" when TA changes
  }, [therapeuticArea]);

  // Compute comma-separated disease names for display and submission
  const selectedDiseasesCommaSeparated = useMemo(() => {
    if (!taxonomyNodes || selectedDiseaseIds.size === 0) return "";
    const nodeMap = new Map(taxonomyNodes.map((n) => [n.id, n]));
    const names: string[] = [];
    for (const id of selectedDiseaseIds) {
      const node = nodeMap.get(id);
      if (node) names.push(node.name);
    }
    return names.join(", ");
  }, [taxonomyNodes, selectedDiseaseIds]);

  // Handle Submit button click — set submittedDiseases to trigger API refetch
  const handleSubmitDiseases = useCallback(() => {
    setSubmittedDiseases(selectedDiseasesCommaSeparated);
  }, [selectedDiseasesCommaSeparated]);

  // Track previous selection count to detect when all diseases are cleared
  const prevSelectionCountRef = useRef(selectedDiseaseIds.size);

  // When all diseases are cleared after having some selected, refresh with "All"
  useEffect(() => {
    const prevCount = prevSelectionCountRef.current;
    const currentCount = selectedDiseaseIds.size;

    // If user had selected diseases but now cleared all (went from >0 to 0)
    if (prevCount > 0 && currentCount === 0) {
      // Reset submittedDiseases to "" and increment refresh key to trigger API refresh with "All"
      setSubmittedDiseases("");
      setDiseaseRefreshKey((k) => k + 1);
    }

    prevSelectionCountRef.current = currentCount;
  }, [selectedDiseaseIds.size]);
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const q = debouncedGeneQuery.trim();
    if (q.length < 1) {
      geneAutocompleteLastHitsRef.current = [];
      setGeneAutocompleteResults([]);
      setShowGeneAutocomplete(false);
      setGeneAutocompleteLoading(false);
      return;
    }
    let cancelled = false;
    // After picking from the list, debounced query still updates — do not reopen the panel
    // (otherwise a fetch can finish with [] and flash "No matching genes").
    if (!suppressGeneAutocompleteRef.current) {
      setShowGeneAutocomplete(true);
    }
    setGeneAutocompleteLoading(true);
    geneDiseaseApi
      .geneAutocomplete(q, 10)
      .then((res) => {
        if (cancelled) return;
        setGeneAutocompleteLoading(false);
        if (suppressGeneAutocompleteRef.current) {
          suppressGeneAutocompleteRef.current = false;
          geneAutocompleteLastHitsRef.current = [];
          setGeneAutocompleteResults([]);
          setShowGeneAutocomplete(false);
          setGeneAutocompleteIndex(-1);
          return;
        }
        if (res.results.length > 0) {
          geneAutocompleteLastHitsRef.current = res.results;
        } else {
          geneAutocompleteLastHitsRef.current = [];
        }
        setGeneAutocompleteResults(res.results);
        setShowGeneAutocomplete(true);
        setGeneAutocompleteIndex(-1);
      })
      .catch(() => {
        if (cancelled) return;
        setGeneAutocompleteLoading(false);
        geneAutocompleteLastHitsRef.current = [];
        setGeneAutocompleteResults([]);
        setShowGeneAutocomplete(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedGeneQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const el = geneAutocompleteContainerRef.current;
      if (el && !el.contains(event.target as Node)) {
        setShowGeneAutocomplete(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const refreshAnalysisTabNavScroll = useCallback(() => {
    const el = analysisTabNavRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setAnalysisTabNavScroll({
      hasOverflow: scrollWidth > clientWidth + 1,
      canLeft: scrollLeft > 2,
      canRight: scrollLeft < maxScroll - 2,
    });
  }, []);

  const scrollAnalysisTabNav = useCallback((dir: "left" | "right") => {
    const el = analysisTabNavRef.current;
    if (!el) return;
    const amount = Math.min(Math.floor(el.clientWidth * 0.75), 320);
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  }, []);

  useLayoutEffect(() => {
    if (!selectedGene) return;
    refreshAnalysisTabNavScroll();
  }, [selectedGene, activeTab, refreshAnalysisTabNavScroll]);

  useEffect(() => {
    if (!selectedGene) return;
    const el = analysisTabNavRef.current;
    if (!el) return;
    refreshAnalysisTabNavScroll();
    const onScroll = () => refreshAnalysisTabNavScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => refreshAnalysisTabNavScroll());
    ro.observe(el);
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      window.removeEventListener("resize", onScroll);
    };
  }, [selectedGene, refreshAnalysisTabNavScroll]);

  const handleGeneAutocompleteSelect = useCallback(
    (result: GeneAutocompleteResult) => {
      suppressGeneAutocompleteRef.current = true;
      geneAutocompleteLastHitsRef.current = [];
      setGeneQuery(result.symbol.toUpperCase());
      setGeneAutocompleteResults([]);
      setShowGeneAutocomplete(false);
      setGeneAutocompleteIndex(-1);
      geneInputRef.current?.focus();
    },
    [],
  );

  const getAutocompleteDisplayRows = useCallback((): GeneAutocompleteResult[] => {
    if (
      geneAutocompleteLoading &&
      geneAutocompleteResults.length === 0 &&
      geneAutocompleteLastHitsRef.current.length > 0
    ) {
      return geneAutocompleteLastHitsRef.current;
    }
    return geneAutocompleteResults;
  }, [geneAutocompleteLoading, geneAutocompleteResults]);

  const noData = {
    title: `${geneQuery.trim()} info not found`,
    content: `Gene ${geneQuery.trim()} not found in cached data`,
  };

  const handleGeneSearch = () => {
    const selGene = geneQuery.trim();
    //  SHOW RESULTS
    setCollapsed(true);
    setResultsVisible(false);
    setSidebarVisible(false);
    setSelectedGene(selGene.toUpperCase());
    setActiveTab("knowledge-graph-associations");

    //find internal value for therapeutic area
    const taObj = therapeuticAreaValues.find(
      (ta) => ta.value === therapeuticArea,
    );
    const therapeuticAreaInternal: string = taObj?.internalValue ?? "";

    //  SHOW RESULTS ENDS
    if (geneQuery.trim()) {
      setLoading(true);
      const reqOne = axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/info?gene_id=${selGene}&indication=${firstUpperCase(
          therapeuticAreaInternal,
        )}`,
      );
      const reqTwo = axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/tissue-expression-data?gene_id=${selGene}&indication=${firstUpperCase(
          therapeuticAreaInternal,
        )}`,
      );
      const reqThree = axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/rnaseq-datasets?indication=${firstUpperCase(
          therapeuticAreaInternal,
        )}`,
      );
      const reqFour = axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/disease-association-scores?gene_id=${selGene}&indication=${firstUpperCase(
          therapeuticAreaInternal,
        )}`,
      );
      const reqFive = axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/disease-associations-known?gene_id=${selGene}&indication=${firstUpperCase(
          therapeuticAreaInternal,
        )}`,
      );
      const reqSix = axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/pathway-associations?gene_id=${selGene}&indication=${firstUpperCase(
          therapeuticAreaInternal,
        )}`,
      );
      axios.all([reqOne, reqTwo, reqThree, reqFour, reqFive, reqSix]).then(
        axios.spread((data1, data2, data3, data4, data5, data6) => {
          setRadioval("genepathwayassociations");
          setTabdata({
            geneinfo: { data: data1.data.data, message: data1.data.message },
            tissueexpression: {
              data: calculateTIssueExpression(data2.data.data),
              message: data2.data.message,
            },
            rnaseqdatasets: {
              data: data3.data.data,
              message:
                data3.data.data[Object.keys(data3.data.data)[0]].description,
            },
            diseaseassociationscores: {
              data: {
                zscore:
                  data4.data.data.length > 0
                    ? data4.data.data.map((a: any) => a["z-score"])
                    : [],
                disease:
                  data4.data.data.length > 0
                    ? data4.data.data.map((a: any) => a["disease"])
                    : [],
              },
              message: data4.data.message,
            },
            diseaseassociationsknown: {
              data: {
                xval:
                  data5.data.data.length > 0
                    ? Object.keys(data5.data.data[0])
                    : [],
                zval:
                  data5.data.data.length > 0
                    ? diseaseassociationsData(data5.data.data[0])
                    : [],
              },
              message: data5.data.message,
            },
            genetbl: {
              data: {
                rows:
                  data6.data.data.length > 0
                    ? data6.data.data.map((itm: any, i: number) => ({
                      ...itm,
                      id: i + 1,
                    }))
                    : [],
                headers: genepathwayassociationsColumns,
              },
              message: data6.data.message,
            },
          });
          scrnaseqapi(
            data3.data.data[Object.keys(data3.data.data)[0]],
            therapeuticAreaInternal,
            geneQuery.trim(),
            setScrnaseqdata,
          );
          setLoading(false);
        }),
      );
    }
  };

  const handleGeneInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const displayRows = getAutocompleteDisplayRows();
    if (showGeneAutocomplete && displayRows.length > 0) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setGeneAutocompleteIndex((prev) =>
            prev < displayRows.length - 1 ? prev + 1 : prev,
          );
          return;
        case "ArrowUp":
          e.preventDefault();
          setGeneAutocompleteIndex((prev) => (prev > 0 ? prev - 1 : -1));
          return;
        case "Enter":
        case "Tab":
          if (geneAutocompleteIndex >= 0) {
            e.preventDefault();
            handleGeneAutocompleteSelect(
              displayRows[geneAutocompleteIndex],
            );
            return;
          }
          break;
      }
    }
    if (showGeneAutocomplete && e.key === "Escape") {
      e.preventDefault();
      setShowGeneAutocomplete(false);
      return;
    }
    if (e.key === "Enter") {
      handleGeneSearch();
    }
  };

  const getTabIcon = (tab: TabConfig) => {
    const IconComponent = tab.icon;
    return <IconComponent className="h-4 w-4" />;
  };

  const renderTabContent = () => {
    //find internal value for therapeutic area
    const taObj = therapeuticAreaValues.find(
      (ta) => ta.value === therapeuticArea,
    );
    const therapeuticAreaInternal: string = taObj?.internalValue ?? "";

    switch (activeTab) {
      case "knowledge-graph-associations":
        return (
          <KnowledgeGraphAssociations
            nodata={noData}
            selectedgene={selectedGene}
            generesponse={generesponse}
            therapeuticArea={therapeuticArea}
            therapeuticAreaInternal={therapeuticAreaInternal}
            setgeneresponse={setGeneresponse}
            taxonomyNodes={taxonomyNodes}
            selectedDiseaseIds={selectedDiseaseIds}
            onDiseaseSelectionChange={setSelectedDiseaseIds}
          />
        );

      case "expression-summaries":
        return (
          <ExpressionSummaries
            nodata={noData}
            selectedgene={selectedGene}
            generesponse={generesponse}
            therapeuticArea={therapeuticArea}
            therapeuticAreaInternal={therapeuticAreaInternal}
            setgeneresponse={setGeneresponse}
            taxonomyNodes={taxonomyNodes}
            selectedDiseaseIds={selectedDiseaseIds}
            onDiseaseSelectionChange={setSelectedDiseaseIds}
          />
        );

      case "known-database-link":
        return (
          <KnownDatabaseLink
            selectedgene={selectedGene}
            generesponse={generesponse}
            setgeneresponse={setGeneresponse}
          />
        );

      case "kg-edge-prediction":
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <ChartBarSquareIcon className="h-8 w-8 mr-3 text-blue-600" />
                KG Edge Prediction
              </h3>
              {selectedGene ? (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      Gene Symbol: {selectedGene}
                    </h4>
                    <p className="text-gray-700">
                      KG Edge Prediction will be displayed here once the backend
                      integration is complete.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-6xl mb-4">🧬</div>
                  <p>Enter a gene symbol to view detailed information</p>
                </div>
              )}
            </div>
          </div>
        );

      case "single-cell-perturbation-prediction":
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <TableCellsIcon className="h-8 w-8 mr-3 text-blue-600" />
                Single Cell Perturbation Prediction
              </h3>
              {selectedGene ? (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      Gene Symbol: {selectedGene}
                    </h4>
                    <p className="text-gray-700">
                      Single Cell Perturbation Prediction will be displayed here
                      once the backend integration is complete.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-6xl mb-4">🧬</div>
                  <p>Enter a gene symbol to view detailed information</p>
                </div>
              )}
            </div>
          </div>
        );

      case "expression-profile":
        return (
          <div className="space-y-6">
            {/*<div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-2xl p-8 border border-green-200">*/}
            <div>
              <div className="flex flex-col space-y-4">
                <ExpressionProfile
                  selectedGene={selectedGene}
                  therapeuticArea={therapeuticArea}
                  taxonomyNodes={taxonomyNodes}
                  selectedDiseaseIds={selectedDiseaseIds}
                  onDiseaseSelectionChange={setSelectedDiseaseIds}
                />
              </div>
            </div>
          </div>
        );

      case "mechanisms":
        return (
          <div className="space-y-6">
            <div className="">
              {/* <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                {getTabIcon(ANALYSIS_TABS.find((tab) => tab.id === activeTab)!)}
                <span className="ml-3">
                  {ANALYSIS_TABS.find((tab) => tab.id === activeTab)?.title}
                </span>
              </h3> */}
              <div className="flex flex-col space-y-4 min-w-0 max-w-full">
                <Mechanisms
                  selectedGene={selectedGene}
                  therapeuticArea={therapeuticArea}
                  taxonomyNodes={taxonomyNodes}
                  selectedDiseaseIds={selectedDiseaseIds}
                  onDiseaseSelectionChange={setSelectedDiseaseIds}
                  selectedDiseases={selectedDiseasesCommaSeparated}
                  submittedDiseases={submittedDiseases}
                  onSubmitDiseases={handleSubmitDiseases}
                  diseaseRefreshKey={diseaseRefreshKey}
                />
              </div>
            </div>
          </div>
        );

      case "literature-explorer":
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
              </div>
            }
          >
            <LiteratureExplorer />
          </Suspense>
        );

      case "target-dossier":
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <InformationCircleIcon className="h-8 w-8 mr-3 text-blue-600" />
                Target Dossier
              </h3>
              {selectedGene ? (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      Gene Symbol: {selectedGene}
                    </h4>
                    <p className="text-gray-700">
                      Target Dossier will be displayed here once the backend
                      integration is complete.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-6xl mb-4">🧬</div>
                  <p>Enter a gene symbol to view detailed information</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                {getTabIcon(ANALYSIS_TABS.find((tab) => tab.id === activeTab)!)}
                <span className="ml-3">
                  {ANALYSIS_TABS.find((tab) => tab.id === activeTab)?.title}
                </span>
              </h3>
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">🚧</div>
                <p>This analysis module is coming soon!</p>
                <p className="text-sm mt-2">
                  {
                    ANALYSIS_TABS.find((tab) => tab.id === activeTab)
                      ?.description
                  }
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string>("");

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setSearchError("");
      // For this hub, we'll create a basic search that helps users find genes
      // Since we don't have a dedicated search API, we'll create mock results for common genes
      const mockGenes = [
        { symbol: "BRCA1", name: "BRCA1 DNA Repair Associated", id: "BRCA1" },
        { symbol: "BRCA2", name: "BRCA2 DNA Repair Associated", id: "BRCA2" },
        { symbol: "TP53", name: "Tumor Protein P53", id: "TP53" },
        {
          symbol: "EGFR",
          name: "Epidermal Growth Factor Receptor",
          id: "EGFR",
        },
        { symbol: "KRAS", name: "KRAS Proto-Oncogene", id: "KRAS" },
        { symbol: "PTEN", name: "Phosphatase And Tensin Homolog", id: "PTEN" },
        {
          symbol: "APC",
          name: "APC Regulator Of WNT Signaling Pathway",
          id: "APC",
        },
        {
          symbol: "PIK3CA",
          name: "Phosphatidylinositol-4,5-Bisphosphate 3-Kinase Catalytic Subunit Alpha",
          id: "PIK3CA",
        },
      ];

      const filtered = mockGenes.filter(
        (gene) =>
          gene.symbol.toLowerCase().includes(query.toLowerCase()) ||
          gene.name.toLowerCase().includes(query.toLowerCase()),
      );

      setSearchResults(filtered);
    } catch (error) {
      console.error("Search error:", error);
      setSearchError("Search functionality coming soon.");
    } finally {
      setIsSearching(false);
    }
  };

  const autocompleteDisplayRows = getAutocompleteDisplayRows();

  return (
    <div className="min-h-full w-full bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* TargetX Header */}
      <TargetXHeader
        title="Gene-Disease Explorer"
        subtitle="Analyze gene-disease associations and molecular mechanisms with comprehensive evidence"
        searchPlaceholder="Search genes, diseases, pathways..."
        onSearch={handleSearch}
      />

      {/* Search Results Section */}
      {searchResults.length > 0 && (
        <div className="px-8 py-4 bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Search Results
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {searchResults.map((gene: any) => (
                <div
                  key={gene.id}
                  className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 cursor-pointer hover:bg-emerald-100 transition-colors"
                  onClick={() => {
                    setGeneQuery(gene.symbol);
                    setSelectedGene(gene.symbol);
                    setSearchResults([]);
                  }}
                >
                  <div className="font-semibold text-emerald-900">
                    {gene.symbol}
                  </div>
                  <div className="text-sm text-gray-600">{gene.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isSearching && (
        <div className="px-8 py-4 bg-yellow-50 border-b border-yellow-200">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-600">Searching...</span>
          </div>
        </div>
      )}

      {searchError && (
        <div className="px-8 py-4 bg-red-50 border-b border-red-200">
          <div className="text-sm text-red-600">{searchError}</div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 px-8 py-6 overflow-hidden space-x-6">
        {/* Left Sidebar Toggle Button - Fixed Position */}
        {!sidebarVisible && (
          <button
            onClick={() => setSidebarVisible(true)}
            className="fixed left-6 top-1/2 transform -translate-y-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-r-lg shadow-xl transition-all duration-300 flex items-center space-x-2"
            title="Show Gene Search"
          >
            <span className="text-sm font-medium">Search</span>
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        )}

        {/* Left Sidebar - Gene Search */}
        {sidebarVisible && (
          <div
            className="w-80 flex flex-col relative bg-white dark:bg-gradient-to-br dark:from-slate-800 dark:to-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 
        shadow-xl hover:shadow-2xl transition-all duration-300 overflow-visible"
          >
            <button
              onClick={() => setSidebarVisible(false)}
              className="absolute right-4 top-4 z-10 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded-full p-2 transition-all duration-200 transform hover:scale-110"
              title="Hide Gene Search"
            >
              <EyeSlashIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>

            <div className="p-6 pt-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-purple-400">
                  Search Filters
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Find genes and explore therapeutic areas
                </p>
              </div>

              <div className="space-y-5">
                {/* Therapeutic Area Section */}
                <div>
                  <label
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2.5"
                    htmlFor="therapeutic-areaID"
                  >
                    <span className="flex items-center">Therapeutic Area</span>
                  </label>
                  <select
                    value={therapeuticArea}
                    name="therapeutic-area"
                    id="therapeutic-areaID"
                    onChange={(e) => setTherapeuticArea(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 transition-all duration-200 font-medium hover:border-blue-400 dark:hover:border-blue-500"
                  >
                    <option value="" className="bg-white dark:bg-slate-800">
                      Select an option
                    </option>
                    {therapeuticAreaValues.map((itm) => (
                      <option
                        key={itm.value}
                        value={itm.value}
                        disabled={itm.disabled}
                        className="bg-white dark:bg-slate-800"
                      >
                        {itm.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gene Symbol Section */}
                <div>
                  <label
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2.5"
                    htmlFor="geneInputID"
                  >
                    <span className="flex items-center">Gene Symbol</span>
                  </label>
                  <div className="relative" ref={geneAutocompleteContainerRef}>
                    <input
                      ref={geneInputRef}
                      id="geneInputID"
                      type="text"
                      autoComplete="off"
                      value={geneQuery}
                      onChange={(e) => {
                        suppressGeneAutocompleteRef.current = false;
                        setGeneQuery(e.target.value.toUpperCase());
                      }}
                      onKeyDown={handleGeneInputKeyDown}
                      onFocus={() => {
                        // Selecting an item focuses the input again; do not reopen an empty list.
                        if (suppressGeneAutocompleteRef.current) return;
                        if (
                          debouncedGeneQuery.trim().length > 0 &&
                          (geneAutocompleteResults.length > 0 ||
                            geneAutocompleteLoading)
                        ) {
                          setShowGeneAutocomplete(true);
                        }
                      }}
                      placeholder="Type to search genes…"
                      aria-busy={geneAutocompleteLoading}
                      className={`w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 font-medium hover:border-purple-400 dark:hover:border-purple-500 ${
                        geneQuery ||
                        (geneAutocompleteLoading &&
                          debouncedGeneQuery.trim().length > 0)
                          ? "pr-20"
                          : ""
                      }`}
                    />
                    {geneAutocompleteLoading &&
                      debouncedGeneQuery.trim().length > 0 && (
                        <div
                          className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none"
                          aria-hidden
                        >
                          <div
                            className="h-5 w-5 animate-spin rounded-full border-2 border-purple-500 border-t-transparent dark:border-purple-400 dark:border-t-transparent"
                            role="status"
                          />
                        </div>
                      )}
                    {geneQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          suppressGeneAutocompleteRef.current = false;
                          geneAutocompleteLastHitsRef.current = [];
                          setGeneQuery("");
                          setGeneAutocompleteResults([]);
                          setShowGeneAutocomplete(false);
                          setGeneAutocompleteLoading(false);
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                    {debouncedGeneQuery.trim().length > 0 &&
                      showGeneAutocomplete && (
                        <div
                          className={`absolute left-0 right-0 top-full z-50 mt-1 rounded-xl shadow-lg border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 max-h-60 overflow-y-auto overflow-x-hidden transition-opacity duration-200 ${
                            geneAutocompleteLoading &&
                            autocompleteDisplayRows.length > 0
                              ? "opacity-70 pointer-events-none"
                              : "opacity-100"
                          }`}
                          role="listbox"
                          aria-label="Gene suggestions"
                          aria-busy={geneAutocompleteLoading}
                        >
                          {geneAutocompleteLoading &&
                          autocompleteDisplayRows.length === 0 ? (
                            <div
                              className="p-3 space-y-2 min-h-[3.25rem]"
                              aria-hidden
                            >
                              {[0, 1, 2].map((i) => (
                                <div
                                  key={i}
                                  className="h-10 rounded-lg bg-slate-200 dark:bg-slate-600 animate-pulse"
                                />
                              ))}
                            </div>
                          ) : null}
                          {!geneAutocompleteLoading &&
                          geneAutocompleteResults.length === 0 &&
                          !suppressGeneAutocompleteRef.current ? (
                            <div
                              className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400"
                              role="presentation"
                            >
                              No matching genes
                            </div>
                          ) : null}
                          {autocompleteDisplayRows.map((result, index) => (
                            <button
                              key={`${result.ensembl_id}-${result.symbol}`}
                              type="button"
                              role="option"
                              aria-selected={index === geneAutocompleteIndex}
                              title={
                                result.name?.trim()
                                  ? result.name.trim()
                                  : undefined
                              }
                              onMouseEnter={() =>
                                setGeneAutocompleteIndex(index)
                              }
                              onClick={() =>
                                handleGeneAutocompleteSelect(result)
                              }
                              className={`w-full px-3 py-2 text-left font-semibold text-sm transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0 ${index === geneAutocompleteIndex
                                ? "bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100"
                                : "hover:bg-gray-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100"
                                }`}
                            >
                              {result.symbol}
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleGeneSearch}
                    disabled={!geneQuery.trim() || !therapeuticArea.trim()}
                    className={`w-full flex items-center justify-center px-4 py-3 font-bold rounded-xl transition-all duration-200 transform ${geneQuery.trim() && therapeuticArea.trim()
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer"
                      : "bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      }`}
                  >
                    <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                    Search Gene
                  </button>
                  <button
                    onClick={() => {
                      suppressGeneAutocompleteRef.current = false;
                      geneAutocompleteLastHitsRef.current = [];
                      setGeneQuery("");
                      setTherapeuticArea("");
                      setSelectedGene("");
                      setSelectedDiseaseIds(new Set());
                      setGeneAutocompleteResults([]);
                      setShowGeneAutocomplete(false);
                      setGeneAutocompleteIndex(-1);
                    }}
                    className="w-full px-4 py-2.5 font-medium rounded-xl border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-all duration-200 text-sm"
                  >
                    Reset
                  </button>
                </div>

                {/* Selected Gene Display */}
                {selectedGene && (
                  <div className="mt-6 pt-5 border-t-2 border-gray-200 dark:border-slate-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Active Gene
                    </p>
                    <div className="px-3 py-2 bg-blue-50 dark:bg-slate-700 rounded-lg border border-blue-200 dark:border-slate-600">
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-400">
                        {selectedGene}
                      </p>
                      {therapeuticArea && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {
                            therapeuticAreaValues.find(
                              (a) => a.value === therapeuticArea,
                            )?.label
                          }
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Center Content Area */}
        <div className="flex-1 rounded-xl shadow-lg flex flex-col min-w-0 overflow-hidden dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
          {!selectedGene ? (
            <div className="flex items-center justify-center h-full p-8">
              <div className="text-center max-w-xl">
                <div className="text-6xl mb-6">🔗</div>
                <h3 className="text-2xl font-bold mb-4 text-slate-800 dark:text-slate-100 transition-colors">
                  Search for a Gene
                </h3>
                <p className="text-slate-800 dark:text-slate-400 transition-colors leading-relaxed">
                  Enter a gene symbol to explore disease associations and
                  molecular mechanisms
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Horizontal Tab Navigation — scroll via arrows; scrollbar hidden */}
              <div className="border-b border-gray-200 dark:border-gray-600 flex items-stretch min-h-[3rem]">
                {analysisTabNavScroll.hasOverflow &&
                  analysisTabNavScroll.canLeft && (
                  <div className="flex items-center shrink-0 pl-2">
                    <button
                      type="button"
                      aria-label="Scroll tabs left"
                      onClick={() => scrollAnalysisTabNav("left")}
                      className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
                <nav
                  ref={analysisTabNavRef}
                  className={`flex flex-1 min-w-0 space-x-1 items-stretch overflow-x-auto overflow-y-hidden scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${
                    analysisTabNavScroll.hasOverflow
                      ? `${analysisTabNavScroll.canLeft ? "pl-1" : "pl-6"} ${analysisTabNavScroll.canRight ? "pr-1" : "pr-6"}`
                      : "px-6"
                  }`}
                >
                  {ANALYSIS_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 whitespace-nowrap ${activeTab === tab.id
                        ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600"
                        }`}
                    >
                      {getTabIcon(tab)}
                      <span>{tab.title}</span>
                    </button>
                  ))}
                </nav>
                {analysisTabNavScroll.hasOverflow &&
                  analysisTabNavScroll.canRight && (
                  <div className="flex items-center shrink-0 pr-2">
                    <button
                      type="button"
                      aria-label="Scroll tabs right"
                      onClick={() => scrollAnalysisTabNav("right")}
                      className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-auto p-6">
                {renderTabContent()}
              </div>
            </>
          )}
        </div>

        {/* Right Panel Toggle Button based on !resultsVisible */}
        {selectedGene && !resultsVisible && (
          // <button
          //   onClick={() => setResultsVisible(true)}
          //   className="fixed right-6 top-1/2 transform -translate-y-1/2 z-50 bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-l-lg shadow-xl transition-all duration-300 flex items-center space-x-2"
          //   title="Show Analysis Panel"
          // >
          //   <ChevronLeftIcon className="h-5 w-5" />
          //   <span className="text-sm font-medium">Actions</span>
          // </button>

          <button
            className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40 bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-l-lg shadow-xl transition-all duration-300 flex items-center group"
            onClick={() =>
              window.open(
                "https://forms.microsoft.com/pages/responsepage.aspx?id=e7Oy_KBda0abgwAUtnp8eAHXS69n1rhMlaofIdilpHZUMFcyR1lKSTNHMURPRU5LU1FJQ04xSkcxNS4u&route=shorturl",
                "_blank",
              )
            }
            aria-label="Feedback"
          >
            <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
            <span
              className="text-sm font-medium ml-0 transition-all duration-200 opacity-0 max-w-0 group-hover:opacity-100 group-hover:max-w-xs group-hover:ml-2"
              style={{ whiteSpace: "nowrap" }}
            >
              Feedback
            </span>
          </button>
        )}

        {/* Right Analysis Panel based on resultsVisible */}
        {selectedGene && resultsVisible && (
          <div className="w-72 flex-shrink-0 fixed right-0 bg-gray-100">
            <button
              onClick={() => setResultsVisible(false)}
              className="absolute right-3 top-3 z-8 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-colors"
              title="Hide Analysis Panel"
            >
              <EyeSlashIcon className="h-4 w-4 text-gray-500" />
            </button>

            {/*}
            // Quick Stats Card
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
              <h3 className="text-base font-bold text-gray-900 mb-4">
                Quick Stats
              </h3>

              {selectedGene ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <span className="text-xs font-medium text-blue-700">
                      Associations
                    </span>
                    <span className="text-lg font-bold text-blue-900">
                      {diseaseAssociations.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                    <span className="text-xs font-medium text-green-700">
                      Tissues
                    </span>
                    <span className="text-lg font-bold text-green-900">
                      {tissueExpression.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                    <span className="text-xs font-medium text-purple-700">
                      Mechanisms
                    </span>
                    <span className="text-lg font-bold text-purple-900">
                      {Object.keys(mechanisms).length}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-3xl mb-3">📊</div>
                  <p className="text-sm">Search gene to view stats</p>
                </div>
              )}
            </div>
            {*/}

            {/* Action Panel  */}
            <div
              className="rounded-xl p-4 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 
        shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-base font-bold mb-4 text-slate-800 dark:text-slate-100 transition-colors">
                Actions
              </h3>
              {selectedGene ? (
                <div className="space-y-3">
                  <button
                    className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium text-sm rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 shadow-md"
                    onClick={() =>
                      window.open(
                        "https://forms.microsoft.com/pages/responsepage.aspx?id=e7Oy_KBda0abgwAUtnp8eAHXS69n1rhMlaofIdilpHZUMFcyR1lKSTNHMURPRU5LU1FJQ04xSkcxNS4u&route=shorturl",
                        "_blank",
                      )
                    }
                  >
                    Feedback&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;
                    <LinkIcon className="h-5 w-5 inline-block" />
                  </button>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-3xl mb-3">⚙️</div>
                  <p className="text-sm">Search gene to view actions</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
